<template>
  <div :style="getContainerStyle">
    <ModalAddRequiredSkills
      @add-required-skills="skillNames => addTenantTeamSkills(skillNames, true)"
    />
    <ModalDeleteTenantTeam />
    <ModalDeleteTenantTeamTenantUser
      @delete-tenant-team-tenant-user="deleteTenantTeamTenantUser"
    />
    <ModalRenameTenantTeam
      @renamed-tenant-team="newTenantTeamName => renameTenantTeam(newTenantTeamName)"
    />
    <ModalShareTenantTeamLink
      @updated-tenant-team-permissions="$emit('updated-tenant-team-permissions')"
    />
    <Header
      :tenantTeam="tenantTeam ? tenantTeam : {}"
      :tenantTeamPermissions="tenantTeamPermissions"
      :viewOnly="viewOnly"
      :style="style.header"
    />
    <div :style="getInnerContainerStyle">
      <div :style="getLeftContainerStyle">
        <div :style="getRequiredSkillsContainerStyle">
          <div :style="style.headerContainer">
            <HeaderMediumMuted>
              REQUIRED SKILLS
            </HeaderMediumMuted>
            <ButtonTextSecondary
              v-if="!viewOnly"
              @click="openModalToAddRequiredSkills"
            >
              ADD
            </ButtonTextSecondary>
          </div>
          <RequiredSkills
            :isLoading="!loadedTenantTeamSkills"
            :requiredSkills="requiredSkills"
            :viewOnly="viewOnly"
            @delete-required-skill="deleteRequiredSkill"
            :style="style.requiredSkills"
          />
          <SkillsGap
            :missedSkills="missedSkills"
            :requiredSkills="requiredSkills"
            :style="style.skillsGap"
          />
        </div>
        <div
          ref="skillsContainer"
          :style="getSkillsContainerStyle"
        >
          <div :style="style.skillsHeaderContainer">
            <HeaderMediumMuted>
              {{ skillsSectionTitle }}
            </HeaderMediumMuted>
            <div :style="style.paginationContainer">
              <PaginationSecondary
                v-model="skillsSectionPage"
                :pageSize="2"
              />
            </div>
          </div>
          <SkillsGraph
            v-if="skillsSectionPage === 1"
            :edges="visibleSkillEdges"
            :isLoading="(
              !loadedTenantTeamTenantUserSkills ||
              !loadedTenantTeamTenantUsers ||
              !loadedTenantTeamSkills
            )"
            :nodes="visibleSkillNodes"
            :nodeIdsOnHover="visibleSkillNodeIdsOnHover"
            :parentContainerWidth="skillsContainerWidth"
            :tenantUserIdOnHover="tenantUserIdOnHover"
            :style="style.skillsGraph"
          />
          <SkillsDependency
            v-else
            :isLoading="!loadedTenantTeamTenantUserSkills"
            :requiredSkills="requiredSkills"
            :skillNodes="visibleSkillNodes"
            :style="style.skillsDependency"
          />
        </div>
      </div>
      <div :style="getRightContainerStyle">
        <TabPrimary
          v-model="tabIndex"
          :initSelectedTabIndex="0"
          :tabs="tabs"
          :style="style.tab"
        />
        <template v-if="tabIndex === 0">
          <UserList
            :isLoading="!loadedTenantTeamTenantUsers"
            :tenantTeam="tenantTeam"
            :tenantTeamTenantUsers="tenantTeamTenantUsers"
            :viewOnly="viewOnly"
            @mouseout-tenant-user="onMouseoutTenantUserInUserList"
            @mouseover-tenant-user="onMouseoverTenantUserInUserList"
            @search-by-person-name="searchExpertByPersonName"
            @search-by-skill-name="searchExpertBySkillName"
            @selected="updateTenantTeamTenantUser"
            @unselected="updateTenantTeamTenantUser"
          />
        </template>
        <template v-else-if="tabIndex === 1">
          <SkillList
            :isLoading="(
              !loadedTenantTeamTenantUserSkills ||
              !loadedTenantTeamSkills ||
              !loadedTenantTeamSkills
            )"
            :skillNodes="skillNodes"
            :tenantTeamId="tenantTeam.id"
            :viewOnly="viewOnly"
            :visibleTenantUsers="visibleTenantUsers"
            @add-required-skill="skill => addTenantTeamSkill(skill, true)"
            @delete-required-skill="deleteRequiredSkill"
            @selected="skill => addTenantTeamSkill(skill)"
            @unselected="deleteTenantTeamSkill"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
  import ApiSkillByName from '@js/api/skill-by-name'
  import ApiTenantTeamInvisibleSkill from '@js/api/tenant-team-invisible-skill'
  import ApiTenantTeamSkill from '@js/api/tenant-team-skill'
  import ApiTenantTeamTenantUser from '@js/api/tenant-team-tenant-user'
  import ButtonTextSecondary from '@modules/button/button-text-secondary'
  import Header from '@teamup/modules/header'
  import HeaderMediumMuted from '@modules/text/header-medium-muted'
  import ModalAddRequiredSkills from '@teamup/modals/modal-add-required-skills'
  import ModalDeleteTenantTeam from '@teamup/modals/modal-delete-tenant-team'
  import ModalDeleteTenantTeamTenantUser from '@teamup/modals/modal-delete-tenant-team-tenant-user'
  import ModalRenameTenantTeam from '@teamup/modals/modal-rename-tenant-team'
  import ModalShareTenantTeamLink from '@teamup/modals/modal-share-tenant-team-link'
  import PaginationSecondary from '@modules/pagination/pagination-secondary'
  import RequiredSkills from '@teamup/modules/required-skills'
  import SkillsDependency from '@teamup/modules/skills-dependency'
  import SkillsGap from '@teamup/modules/skills-gap'
  import SkillsGraph from '@teamup/modules/skills-graph'
  import SkillList from '@teamup/modules/skill-list'
  import TabPrimary from '@modules/tab/tab-primary'
  import UserList from '@teamup/modules/user-list'
  import { getSkillIdToTenantUserIds } from '@js/teamup/utils'
  import { Layout } from '@js/styles'
  import { mapGetters, mapState } from 'vuex'
  import { MODAL_WINDOW_NAMES, SEARCH_EXPERT_PARAMS } from '@js/constants'

  const requiredSkillsHeight = '103px';
  const skillsGapHeight = '42px';

  const teamupHeaderMarginTop = 24;
  const teamupHeaderMarginBottom = 16;
  const teamupHeaderHeight = `${teamupHeaderMarginTop + teamupHeaderMarginBottom + 72}px`;

  export default {
    name: 'TeamupTeamView',
    components: {
      ButtonTextSecondary,
      Header,
      HeaderMediumMuted,
      ModalAddRequiredSkills,
      ModalDeleteTenantTeam,
      ModalDeleteTenantTeamTenantUser,
      ModalRenameTenantTeam,
      ModalShareTenantTeamLink,
      PaginationSecondary,
      RequiredSkills,
      SkillList,
      SkillsDependency,
      SkillsGap,
      SkillsGraph,
      TabPrimary,
      UserList,
    },
    props: {
      tenantTeamPermissions: {
        type: Array,
        required: false,
        default: () => [],
      },
    },
    data: function () {
      return {
        skillsContainerWidth: 0,
        skillsSectionPage: 1,
        tabIndex: 0,
        tabs: [
          {
            label: 'Members',
            disabled: false,
          },
          {
            label: 'Skills',
            disabled: false,
          },
        ],
        tenantTeamInvisibleSkills: [],
        tenantUserIdOnHover: null,
        verticalLayout: false,
        verticalLayoutWidthThresh: 840,
        style: {
          headerContainer: {
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          },
          header: {
            margin: `${Layout.space.lg} 0 ${Layout.space.md} 0`,
          },
          headerText: {
            padding: `0 ${Layout.space.sm}`,
          },
          paginationContainer: {
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'flex-end',
          },
          requiredSkills: {
            margin: `${Layout.space.sm} 0 0 0`,
          },
          skillsDependency: {
            height: 'calc(100% - 30px)',
            margin: `${Layout.space.sm} 0 0 0`,
          },
          skillsGap: {
            margin: `${Layout.space.sm} 0 0 0`,
          },
          skillsGraph: {
            height: 'calc(100% - 30px)',
            margin: `${Layout.space.sm} 0 0 0`,
          },
          skillsHeaderContainer: {
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
          },
          tab: {
            width: '100%',
          },
        },
      };
    },
    mounted: async function () {
      this.skillsContainerWidth = this.$refs.skillsContainer.clientWidth;
      this.verticalLayout = this.windowWidth < this.verticalLayoutWidthThresh;
    },
    computed: {
      ...mapGetters('teamup', ['loaded']),
      ...mapState({
        tenantTeam: state => state.teamup.data.tenantTeam,
        tenantTeamSkills: state => state.teamup.data.tenantTeamSkills,
        tenantTeamTenantUsers: state => state.teamup.data.tenantTeamTenantUsers,
        tenantTeamTenantUserSkills: state => (
          state.teamup.data.tenantTeamTenantUserSkills),
        viewOnly: state => state.teamup.viewOnly,
        windowWidth: state => state.window.width,
      }),
      getContainerStyle: function () {
        return {
          display: 'flex',
          flexDirection: 'column',
          margin: Layout.container.primary.margin,
          maxWidth: Layout.container.primary.maxWidth,
          minHeight: '600px',
          padding: '0 12px',
          width: '100%',
          ...(this.verticalLayout ? {
          } : {
            height: `calc(100vh - ${Layout.header.height})`,
          }),
        };
      },
      getInnerContainerStyle: function () {
        return {
          display: 'flex',
          height: `calc(100% - ${teamupHeaderHeight})`,
          padding: `0 0 ${Layout.space.lg} 0`,
          width: '100%',
          ...(this.verticalLayout ? {
            flexDirection: 'column',
          } : {}),
        };
      },
      getLeftContainerStyle: function () {
        return {
          ...(this.verticalLayout ? {
            height: '775px',
            width: '100%',
          } : {
            alignSelf: 'stretch',
            flex: 2,
            minWidth: 0, // Caution!: This is necessary not to overflow x direction
          }),
        };
      },
      getRequiredSkillsContainerStyle: function () {
        return {
          display: 'flex',
          flexDirection: 'column',
          padding: `0 12px 0 12px`,
          height: `calc(${requiredSkillsHeight} + ${skillsGapHeight})`,
        };
      },
      getRightContainerStyle: function () {
        return {
          padding: '0 12px',
          ...(this.verticalLayout ? {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            height: '600px',
            justifyContent: 'flex-start',
            margin: `${Layout.space.lg} 0 0 0`,
            width: '100%',
          } : {
            alignSelf: 'stretch',
            height: '100%',
            maxWidth: '400px',
            minWidth: '240px',
            flex: 1,
          }),
        };
      },
      getSkillsContainerStyle: function () {
        return {
          padding: `${Layout.space.md} 12px 0 12px`,
          ...(this.verticalLayout ? {
            height: '640px',
            width: '100%',
          } : {
            height: `calc(100% - ${requiredSkillsHeight} - ${skillsGapHeight})`,
          }),
        };
      },
      loadedTenantTeamSkills: function () {
        return this.loaded('tenantTeamSkills');
      },
      loadedTenantTeamTenantUsers: function () {
        return this.loaded('tenantTeamTenantUsers');
      },
      loadedTenantTeamTenantUserSkills: function () {
        return this.loaded('tenantTeamTenantUserSkills');
      },
      missedSkills: function () {
        return this.requiredSkills.filter(elem => elem.missed);
      },
      requiredSkills: function () {
        return this.tenantTeamSkills.filter(elem => elem.required).reduce(
          (result, elem) => {
            result.push({
              ...elem.skill,
              label: elem.displayName,
              missed: !(elem.skill.id in this.skillIdToVisibleTenantUserIds),
              tenantTeamSkillId: elem.id,
            });
            return result;
          }, []
        ).sort((e1, e2) => e1.label.localeCompare(e2.label));
      },
      skillIdToTenantTeamSkill: function () {
        return this.tenantTeamSkills.reduce((result, elem) => {
          result[elem.skill.id] = elem;
          return result;
        }, {});
      },
      skillIdToTenantUserIds: function () {
        return getSkillIdToTenantUserIds(this.tenantUsers);
      },
      skillIdToVisibleTenantUserIds: function () {
        return getSkillIdToTenantUserIds(this.visibleTenantUsers);
      },
      skillNodes: function () {
        // Skills shown in skills graph. Invisible user's skills are not contained.
        return this.tenantTeamTenantUserSkills.reduce((result, elem) => {
          if (!this.visibleTenantUserIds.includes(elem.tenantUser.id)) {
            return result;
          }

          const tenantUser = this.tenantUserIdToTenantUser[elem.tenantUser.id];

          const tenantTeamSkill = this.skillIdToTenantTeamSkill[elem.skill.id];
          const tenantTeamSkillId = tenantTeamSkill == null ? null : tenantTeamSkill.id;
          const required = tenantTeamSkill == null ? false : tenantTeamSkill.required;

          const index = result.findIndex(obj => obj.id === elem.skill.id);
          if (index < 0) {
            result.push({
              ...elem.skill,
              neighbors: elem.skill.id in this.skillIdToVisibleConnectedSkillIds ? this.skillIdToVisibleConnectedSkillIds[elem.skill.id] : [],
              required: required,
              tenantTeam: this.tenantTeam,
              tenantTeamSkillId: tenantTeamSkillId == null ? null : tenantTeamSkillId,
              tenantUsers: [tenantUser],
              tenantUserIds: [elem.tenantUser.id],
              tenantUsersLength: 1,
              visible: tenantTeamSkillId != null,
            });
          } else {
            result[index].tenantUsers.push(tenantUser);
            result[index].tenantUserIds.push(elem.tenantUser.id);
            result[index].tenantUsersLength += 1;
          }

          return result;
        }, []).sort((e1, e2) => e1.name.localeCompare(e2.name));
      },
      skillsSectionTitle: function () {
        return this.skillsSectionPage === 1 ? 'TEAM SKILLS GRAPH' : 'TEAM SKILLS DEPENDENCY';
      },
      tenantUsers: function () {
        return this.tenantTeamTenantUsers.map(elem => elem.tenantUser);
      },
      tenantUserIdToTenantUser: function () {
        return this.tenantUsers.reduce((result, elem) => {
          result[elem.id] = elem;
          return result;
        }, {});
      },
      tenantUserSkillIdToSkillId: function () {
        return this.tenantTeamTenantUserSkills.reduce((result, elem) => {
          result[elem.id] = elem.skill.id;
          return result;
        }, {});
      },
      skillIdToVisibleConnectedSkillIds: function () {
        // Object which maps skill id to array of connected skill's id.
        // The given list only includes which are displayed in skills graph.
        return this.tenantTeamTenantUserSkills.filter(
            elem => this.visibleSkillIds.includes(elem.skill.id)).reduce(
                (result, elem) => {
                  result[elem.skill.id] = Array.from(new Set([
                    ...(elem.skill.id in result ? result[elem.skill.id] : []),
                    ...(elem.targets.map(
                      tenantUserSkillId => {
                        return this.tenantUserSkillIdToSkillId[tenantUserSkillId];
                    }).filter(skillId => this.visibleSkillIds.includes(skillId))),
                  ]));
                  return result;
                }, {}
            );
      },
      visibleSkillEdges: function () {
        return [...new Set(
          this.visibleSkillNodes.reduce((parent, elem) => {
            const edges = elem.neighbors.reduce((child, skillId) => {
              const pair1 = { source: elem.id, target: skillId };
              const pair2 = { source: skillId, target: elem.id };
              child.push(JSON.stringify(pair1, Object.keys(pair1).sort()));
              child.push(JSON.stringify(pair2, Object.keys(pair2).sort()));
              return child;
            }, []);
            parent.push(...edges);
            return parent;
          }, []))].map(elem => JSON.parse(elem));
      },
      visibleSkillIds: function () {
        return this.tenantTeamSkills.map(
            elem => elem.skill.id).filter(
              elem => this.skillIdToVisibleTenantUserIds[elem] != null);
      },
      visibleSkillNodes: function () {
        return this.skillNodes.filter(elem => elem.visible);
      },
      visibleSkillNodeIdsOnHover: function () {
        return this.visibleSkillNodes.filter(elem => {
          return this.tenantUserIdOnHover == null ? false : (
            elem.tenantUserIds.includes(this.tenantUserIdOnHover));
        }).map(elem => elem.id);
      },
      visibleTenantUsers: function () {
        return this.tenantTeamTenantUsers.filter(elem => elem.selected).map(
            elem => elem.tenantUser);
      },
      visibleTenantUserIds: function () {
        return this.visibleTenantUsers.map(elem => elem.id)
      },
    },
    methods: {
      addTenantTeamSkill: async function (skill, required = null) {
        const tenantTeamSkill = {
          displayName: skill.name,
          skill: { id: skill.id },
          ...(required != null ? { required: required } : {}),
        };

        if (skill.id in this.skillIdToTenantTeamSkill) {
          tenantTeamSkill.id = await this.skillIdToTenantTeamSkill[skill.id].id;
          tenantTeamSkill.required = await tenantTeamSkill.required != null ? tenantTeamSkill.required : this.skillIdToTenantTeamSkill[skill.id].required;
          await this.updateTenantTeamSkills([tenantTeamSkill]);
        } else {
          await this.createTenantTeamSkills([tenantTeamSkill]);
        }
      },
      addTenantTeamSkills: async function (skillNames, required) {
        const skills = await this.fetchSkillsBySkillNames(skillNames);
        if (skills == null) {
          return;
        }

        const skillIdToDisplayName = await skills.reduce((result, elem) => {
          result[elem.id] = elem.byName;
          return result;
        }, {});

        const tenantTeamSkillsToCreate = await skills.filter(
          elem => !(elem.id in this.skillIdToTenantTeamSkill)).reduce(
            (result, elem) => {
              result.push({
                displayName: skillIdToDisplayName[elem.id],
                required: required,
                skill: { id: elem.id },
              });
              return result;
            }, []);
        const tenantTeamSkillsToUpdate = await skills.filter(
          elem => elem.id in this.skillIdToTenantTeamSkill).reduce(
            (result, elem) => {
              result.push({
                displayName: skillIdToDisplayName[elem.id],
                id: this.skillIdToTenantTeamSkill[elem.id].id,
                required: required,
                skill: { id: elem.id },
              });
              return result;
            }, []);

        if (tenantTeamSkillsToCreate.length > 0) {
          await this.createTenantTeamSkills(tenantTeamSkillsToCreate);
        }
        if (tenantTeamSkillsToUpdate.length > 0) {
          await this.updateTenantTeamSkills(tenantTeamSkillsToUpdate);
        }
      },
      createTenantTeamSkills: async function (tenantTeamSkills) {
        try {
          const payloads = await tenantTeamSkills.reduce((result, elem) => {
            result.push({
              displayName: elem.displayName,
              required: elem.required,
              skillId: elem.skill.id,
            });
            return result;
          }, []);
          const api = await new ApiTenantTeamSkill(this.tenantTeam.id);
          const res = await api.post(payloads);
          await res.data.forEach(tenantTeamSkill => {
            this.$store.commit(
                'teamup/push', { key: 'tenantTeamSkills', data: tenantTeamSkill });
          });
        } catch (error) {
          this.$store.commit('notification/setWarning', 'Failed to craete skills');
          console.error(error);
        }
      },
      fetchSkillsBySkillNames: async function (skillNames) {
        try {
          const payloads = await skillNames.reduce((result, skillName) => {
            result.push({
              name: skillName,
              createIfNotFound: true,
            });
            return result;
          }, []);
          const api = await new ApiSkillByName();
          const res = await api.post(payloads);
          return res.data;
        } catch (error) {
          this.$store.commit(
              'notification/setWarning', 'Failed to fetch skill ids.');
          console.error(error);
        }
        return null;
      },
      deleteRequiredSkill: async function (requiredSkill) {
        try {
          const skillId = requiredSkill.id;
          if (!(skillId in this.skillIdToTenantTeamSkill)) {
            throw 'Required skill id not found';
          }

          const tenantTeamSkillId = this.skillIdToTenantTeamSkill[skillId].id;
          const ownerExists = skillId in this.skillIdToTenantUserIds;

          if (ownerExists) {
            const tenantTeamSkill = await [{
              id: tenantTeamSkillId,
              required: false,
            }];
            await this.updateTenantTeamSkills(tenantTeamSkill);
          } else {
            await this.deleteTenantTeamSkill(tenantTeamSkillId);
          }
        } catch (error) {
          this.$store.commit(
              'notification/setWarning', 'Failed to remove required skill');
          console.error(error);
        }
      },
      deleteTenantTeamSkill: async function (tenantTeamSkillId) {
        try {
          const api = await new ApiTenantTeamSkill(this.tenantTeam.id, tenantTeamSkillId);
          const res = await api.delete();
          await this.$store.commit(
              'teamup/delete', { key: 'tenantTeamSkills', data: res.data });
        } catch (error) {
          this.$store.commit(
              'notification/setWarning', 'Failed to delete skill');
          console.error(error);
        }
      },
      deleteTenantTeamTenantUser: async function (tenantTeamTenantUser) {
        try {
          const api = await new ApiTenantTeamTenantUser(
              this.tenantTeam.id, tenantTeamTenantUser.id);
          const res = await api.delete();

          const tenantUserId = tenantTeamTenantUser.tenantUser.id;
          await this.tenantTeamTenantUserSkills.filter(
            elem => elem.tenantUser.id == tenantUserId).forEach(elem => {
              this.$store.commit(
                'teamup/delete', { key: 'tenantTeamTenantUserSkills', data: elem });
            });

          await this.$store.commit(
              'teamup/delete', { key: 'tenantTeamTenantUsers', data: res.data });
        } catch (error) {
          this.$store.commit('notification/setWarning', 'Failed to delete user');
          console.error(error);
        }

        this.$store.commit(
            'teamup/delete',
            { key: 'tenantTeamTenantUsers', data: tenantTeamTenantUser });
      },
      getTenantTeamSkillId: function (skillId) {
        const index = this.tenantTeamSkills.findIndex(
          obj => obj.skill.id === skillId);
        return index < 0 ? null : this.tenantTeamSkills[index].id;
      },
      onMouseoutTenantUserInUserList: function (tenantUserId) {
        this.tenantUserIdOnHover = null;
      },
      onMouseoverTenantUserInUserList: function (tenantUserId) {
        this.tenantUserIdOnHover = tenantUserId;
      },
      /*onSearchExperts: function (searchParams) {
        this.$router.push({
          name: 'teamup-search',
          params: {
            code: this.tenantTeam.code,
          },
          query: {
            by: searchParams.by,
            tag: searchParams.tag,
          },
        });
      },*/
      onUnselectedTenantTeamInvisibleSkill: function (tenantTeamInvisibleSkill) {
        const index = this.tenantTeamInvisibleSkills.findIndex(obj => obj.id === tenantTeamInvisibleSkill.id);
        if (index >= 0) {
          return;
        }

        this.tenantTeamInvisibleSkills.splice(0, 0, tenantTeamInvisibleSkill);
      },
      onUnselectedTenantTeamInvisibleSkills: function (tenantTeamInvisibleSkills) {
        const tenantTeamInvisibleSkillIds = this.tenantTeamInvisibleSkills.map(elem => elem.id);
        this.tenantTeamInvisibleSkills = [
          ...this.tenantTeamInvisibleSkills,
          ...tenantTeamInvisibleSkills.filter(
              elem => !tenantTeamInvisibleSkillIds.includes(elem.id)),
        ]
      },
      openModalToAddRequiredSkills: function () {
        const payload = {
          tenantTeamId: this.tenantTeam.id,
          tenantTeamName: this.tenantTeam.name,
          requiredSkills: this.requiredSkills,
        };
        this.$modal.show(MODAL_WINDOW_NAMES.ADD_REQUIRED_SKILLS_PRIMARY, payload);
      },
      renameTenantTeam: function (newTenantTeamName) {
        this.tenantTeam.name = newTenantTeamName;
      },
      searchExpertByPersonName: function (input) {
        if (input.trim().length === 0) {
          return;
        }

        this.$router.push({
          name: 'teamup-search',
          params: {
            code: this.tenantTeam.code,
          },
          query: {
            by: SEARCH_EXPERT_PARAMS.MODE.BY_PERSON_NAME,
            tag: [input],
          },
        });
      },
      searchExpertBySkillName: function (tags) {
        if (tags.filter(elem => elem.trim().length > 0).length === 0) {
          return;
        }
        this.$router.push({
          name: 'teamup-search',
          params: {
            code: this.tenantTeam.code,
          },
          query: {
            by: SEARCH_EXPERT_PARAMS.MODE.BY_SKILL_NAME,
            tag: tags,
          },
        });
      },
      updateTenantTeamSkills: async function (tenantTeamSkills) {
        try {
          const payloads = await tenantTeamSkills.reduce((result, elem) => {
            result.push({
              required: elem.required,
              ...(elem.displayName ? { displayName: elem.displayName } : {}),
              ...(elem.skill ? { skillId: elem.skill.id } : {}),
            });
            return result;
          }, []);
          const tenantTeamSkillIds = await tenantTeamSkills.map(elem => elem.id);
          const api = await new ApiTenantTeamSkill(
            this.tenantTeam.id, tenantTeamSkillIds);
          const responses = await api.put(payloads);
          await responses.forEach(res => {
            this.$store.commit(
                'teamup/update', { key: 'tenantTeamSkills', data: res.data });
          });
        } catch (error) {
          this.$store.commit(
              'notification/setWarning', 'Failed to update skill');
          console.error(error);
        }
      },
      updateTenantTeamTenantUser: function (tenantTeamTenantUser) {
        const index = this.tenantTeamTenantUsers.findIndex(obj => obj.id === tenantTeamTenantUser.id);
        if (index < 0) {
          return;
        }
        this.$set(this.tenantTeamTenantUsers, index, tenantTeamTenantUser);
      },
    },
    watch: {
      windowWidth: function (newVal, oldVal) {
        this.skillsContainerWidth = this.$refs.skillsContainer.clientWidth;
        this.verticalLayout = this.windowWidth < this.verticalLayoutWidthThresh;
      },
    },
  };
</script>
